import React, { useState, useEffect } from 'react';
import { useAuth } from './lib/useAuth';
import { getState, setState, subscribe } from './lib/userStore';
import MedicationsScreen from './screens/MedicationsScreen';
import HomeScreen from './screens/HomeScreen';
import SymptomScreen from './screens/SymptomScreen';
import InsuranceScreen from './screens/InsuranceScreen';
import AuthScreen from './screens/AuthScreen';
import PreventativeScreen from './screens/PreventativeScreen';
import ProfileScreen from './screens/ProfileScreen';
import OnboardingScreen from './screens/OnboardingScreen';
import FindCareScreen from './screens/FindCareScreen';
import ResetPasswordScreen from './screens/ResetPasswordScreen';
import GetCareScreen from './screens/GetCareScreen';
import GetCareResultScreen from './screens/GetCareResultScreen';

// ─── Bottom nav ───────────────────────────────────────────────────────────────
const NAV_TABS = [
  {
    id: 'home', label: 'Home',
    icon: active => (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
        <path d="M3 12l9-9 9 9M5 10v9a1 1 0 001 1h4v-5h4v5h4a1 1 0 001-1v-9" stroke={active ? 'var(--teal)' : 'var(--ink-4)'} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
      </svg>
    ),
  },
  {
    id: 'getCare', label: 'Get Care',
    icon: active => (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
        <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z" fill={active ? 'var(--teal)' : 'var(--ink-4)'}/>
      </svg>
    ),
  },
  {
    id: 'insurance', label: 'Coverage',
    icon: active => (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
        <path d="M20 4H4c-1.11 0-2 .89-2 2v12c0 1.11.89 2 2 2h16c1.11 0 2-.89 2-2V6c0-1.11-.89-2-2-2zm0 14H4v-6h16v6zm0-10H4V6h16v2z" fill={active ? 'var(--teal)' : 'var(--ink-4)'}/>
      </svg>
    ),
  },
  {
    id: 'findCare', label: 'Find Care',
    icon: active => (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
        <path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2" stroke={active ? 'var(--teal)' : 'var(--ink-4)'} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
        <circle cx="9" cy="7" r="4" stroke={active ? 'var(--teal)' : 'var(--ink-4)'} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
        <path d="M23 21v-2a4 4 0 00-3-3.87M16 3.13a4 4 0 010 7.75" stroke={active ? 'var(--teal)' : 'var(--ink-4)'} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
      </svg>
    ),
  },
  {
    id: 'profile', label: 'Profile',
    icon: active => (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
        <path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2M12 11a4 4 0 100-8 4 4 0 000 8z" stroke={active ? 'var(--teal)' : 'var(--ink-4)'} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
      </svg>
    ),
  },
];

// Maps screens that don't have a direct tab to their closest parent tab
function activeTab(screen) {
  if (screen === 'preventative') return 'home';
  if (screen === 'getCare' || screen === 'getCareResult') return 'getCare';
  return screen;
}

function BottomNav({ screen, navigate }) {
  const active = activeTab(screen);
  return (
    <div style={nav.bar}>
      {NAV_TABS.map(tab => {
        const isActive = tab.id === active;
        return (
          <button key={tab.id} style={nav.tab} onClick={() => navigate(tab.id)}>
            {tab.icon(isActive)}
            <span style={{ ...nav.label, color: isActive ? 'var(--teal)' : 'var(--ink-4)' }}>
              {tab.label}
            </span>
          </button>
        );
      })}
    </div>
  );
}

// ─── App ──────────────────────────────────────────────────────────────────────
export default function App() {
  const { user, loading, saveProfile, saveSymptomSession, signOut, addMedication, removeMedication, authEvent, clearAuthEvent } = useAuth();
  const [medications, setMedications] = useState(getState().medications || []);
  useEffect(() => subscribe(s => { if (s.medications !== undefined) setMedications(s.medications || []); }), []);
  const [screen, setScreen]         = useState('home');
  const [prevScreen, setPrevScreen] = useState(null);
  const [showAuth, setShowAuth]     = useState(false);
  const [providerContext, setProviderContext] = useState(null);
  const [triageResult, setTriageResult]         = useState(null); // result from triageEngine
  const [triageAnswers, setTriageAnswers]       = useState(null);
  const [triageProtocolId, setTriageProtocolId] = useState(null);
  const [triageAgeBand, setTriageAgeBand]       = useState(null);
  const [triagePersonType, setTriagePersonType] = useState(null);

  // Track firstName from store — when it becomes set after onboarding, re-render to home
  const [firstName, setFirstName] = useState(getState().firstName);
  useEffect(() => subscribe(s => setFirstName(s.firstName)), []);

  // Request geolocation on mount; store in userStore for routeEngine
  useEffect(() => {
    if (!navigator.geolocation) return;
    navigator.geolocation.getCurrentPosition(
      pos => {
        setState({ userLat: pos.coords.latitude, userLng: pos.coords.longitude });
      },
      () => {
        // Silently ignore denial — routeEngine handles null coords gracefully
      },
      { timeout: 8000, maximumAge: 5 * 60 * 1000 }
    );
  }, []);

  function navigate(to, options = {}) {
    if (to !== screen) setPrevScreen(screen);
    setProviderContext(options.providerContext || null);
    if (to === 'getCareResult' && options.triageResult) {
      setTriageResult(options.triageResult);
      setTriageAnswers(options.answers || null);
      setTriageProtocolId(options.protocolId || null);
      setTriageAgeBand(options.ageBand || null);
      setTriagePersonType(options.personType || 'self');
    }
    setScreen(to);
  }

  function goBack() {
    setScreen(prevScreen || 'home');
    setPrevScreen(null);
  }

  if (loading) {
    return (
      <div style={{ ...shell, alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ fontFamily: 'var(--font-display)', fontSize: '22px', color: 'var(--teal)' }}>
          Clear<em>path</em>
        </div>
      </div>
    );
  }

  if (authEvent === 'PASSWORD_RECOVERY') {
    return (
      <div style={shell}>
        <ResetPasswordScreen onSuccess={clearAuthEvent} />
      </div>
    );
  }

  if (showAuth) {
    return (
      <div style={shell}>
        <AuthScreen
          onSuccess={() => setShowAuth(false)}
          onSkip={() => setShowAuth(false)}
        />
      </div>
    );
  }

  // Show onboarding for signed-in users who haven't set their name yet
  if (user && !firstName) {
    return (
      <div style={shell}>
        <OnboardingScreen saveProfile={saveProfile} />
      </div>
    );
  }

  async function handleSignOut() {
    await signOut();
    setScreen('home');
    setPrevScreen(null);
    setShowAuth(true);
  }

  const props = { navigate, goBack, user, saveProfile, saveSymptomSession, signOut: handleSignOut, onAuthPrompt: () => setShowAuth(true) };

  return (
    <div style={shell}>
      {screen === 'home'          && <HomeScreen          {...props} />}
      {screen === 'symptom'       && <SymptomScreen       {...props} />}
      {screen === 'getCare'       && <GetCareScreen       {...props} />}
      {screen === 'getCareResult' && triageResult && (
        <GetCareResultScreen
          {...props}
          triageResult={triageResult}
          answers={triageAnswers}
          protocolId={triageProtocolId}
          ageBand={triageAgeBand}
          personType={triagePersonType}
        />
      )}
      {screen === 'insurance'    && <InsuranceScreen    {...props} />}
      {screen === 'preventative' && <PreventativeScreen {...props} />}
      {screen === 'profile'      && <ProfileScreen      {...props} />}
      {screen === 'findCare'     && <FindCareScreen     {...props} providerContext={providerContext} />}
      {screen === 'medications'  && <MedicationsScreen  {...props} medications={medications} addMedication={addMedication} removeMedication={removeMedication} />}
      {user && <BottomNav screen={screen} navigate={navigate} />}
    </div>
  );
}

const shell = {
  width: '100%',
  maxWidth: '420px',
  height: '100dvh',
  margin: '0 auto',
  display: 'flex',
  flexDirection: 'column',
  position: 'relative',
  overflow: 'hidden',
  background: 'var(--cream)',
};

const nav = {
  bar: {
    display: 'flex',
    alignItems: 'stretch',
    background: 'var(--surface)',
    borderTop: '1px solid var(--border)',
    flexShrink: 0,
    paddingBottom: 'env(safe-area-inset-bottom)',
  },
  tab: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '3px',
    padding: '10px 4px',
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    fontFamily: 'var(--font-body)',
    WebkitTapHighlightColor: 'transparent',
  },
  label: {
    fontSize: '10px',
    fontWeight: 600,
    letterSpacing: '0.04em',
  },
};
